import React, { useEffect, useState } from "react";
import { Box, Modal } from "@mui/material";
import cssClasses from "./ManualTagModal.module.css";
import Close from "../../Components/Icons/Close";
import Button from "../../Subcomponents/Button";
import CustomInput from "../../Subcomponents/CustomInput";
import LeadStatusChip from "../../Subcomponents/LeadStatusChip";
import Dot from "../../Components/Icons/Dot";
import { errorTost, successTost } from "../../Services/Toast";
import { dismissLoader, presentLoader } from "../../Services/loader";
import {
  getPendingDuesForLead,
  manualTagFromTransactionPayment,
} from "../../Services/Transactions";

function numberWithCommas(x) {
  return x?.toString().replace(/\B(?=(?:(\d\d)+(\d)(?!\d))+(?!\d))/g, ",");
}

export default function ManualTagModal({ open, transaction, handleClose, returnRes }) {
  const [loadingDues, setLoadingDues] = useState(true);
  const [pendingDues, setPendingDues] = useState({ tokens: [], allotments: [] });

  const [selectedTokenId, setSelectedTokenId] = useState(null);
  const [tokenAmount, setTokenAmount] = useState("");

  const [selectedAllotmentId, setSelectedAllotmentId] = useState(null);
  const [allotmentAmount, setAllotmentAmount] = useState("");
  const [ocr, setOcr] = useState("");
  const [gst, setGst] = useState("");
  const [sdr, setSdr] = useState("");

  const [errorMessage, setErrorMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open || !transaction) return;
    setLoadingDues(true);
    getPendingDuesForLead(transaction.leaddisplayid)
      .then((res) => setPendingDues(res || { tokens: [], allotments: [] }))
      .catch(() => setPendingDues({ tokens: [], allotments: [] }))
      .finally(() => setLoadingDues(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, transaction]);

  const selectedToken = pendingDues.tokens.find((t) => t.eventTokenId === selectedTokenId);
  const selectedAllotment = pendingDues.allotments.find(
    (a) => a.allotmentPaymentId === selectedAllotmentId
  );
  const isAllotmentComplete = selectedAllotment?.status === "Payment Complete";

  function closeModal() {
    handleClose();
  }

  function validate() {
    const tAmt = Number(tokenAmount) || 0;
    const aAmt = Number(allotmentAmount) || 0;
    const ocrAmt = Number(ocr) || 0;
    const gstAmt = Number(gst) || 0;
    const sdrAmt = Number(sdr) || 0;

    if (tAmt < 0 || aAmt < 0 || ocrAmt < 0 || gstAmt < 0 || sdrAmt < 0) {
      return "Amounts must be zero or positive numbers.";
    }
    if (tAmt === 0 && aAmt === 0) {
      return "Enter an amount for at least one of Token or Allotment.";
    }
    if (tAmt + aAmt > (transaction?.amount || 0)) {
      return "Token + Allotment amount cannot exceed the untagged amount.";
    }
    if (tAmt > 0 && !selectedToken) {
      return "Select which token this amount should go toward.";
    }
    if (aAmt > 0 && !selectedAllotment) {
      return "Select which allotment this amount should go toward.";
    }
    if (aAmt > 0 && isAllotmentComplete && ocrAmt + gstAmt + sdrAmt !== aAmt) {
      return "OCR + GST + SDR must add up exactly to the allotment amount.";
    }
    return "";
  }

  function submit() {
    const validationError = validate();
    if (validationError) {
      setErrorMessage(validationError);
      return;
    }
    setErrorMessage("");
    setSubmitting(true);
    presentLoader();
    const payload = {
      untaggedTransactionId: transaction.id,
      tokenAmount: Number(tokenAmount) || 0,
      allotmentAmount: Number(allotmentAmount) || 0,
      ocr: isAllotmentComplete ? Number(ocr) || 0 : 0,
      gst: isAllotmentComplete ? Number(gst) || 0 : 0,
      sdr: isAllotmentComplete ? Number(sdr) || 0 : 0,
      eventTokenId: selectedToken?.eventTokenId,
      allotmentPaymentId: selectedAllotment?.allotmentPaymentId,
    };
    manualTagFromTransactionPayment(payload)
      .then((response) => {
        dismissLoader();
        setSubmitting(false);
        if (response?.data?.success) {
          successTost(response?.data?.message || "Transaction tagged successfully.");
          returnRes(true);
          handleClose();
        } else {
          errorTost(response?.data?.message || "Something went wrong ! please try again later.");
        }
      })
      .catch((err) => {
        dismissLoader();
        setSubmitting(false);
        if (err?.response?.data?.message) {
          errorTost(err.response.data.message);
        } else {
          errorTost("Something went wrong ! please try again later.");
        }
      });
  }

  return (
    <Modal
      open={true}
      onClose={(_, reason) => {
        if (reason !== "backdropClick") {
          closeModal();
        }
      }}
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description"
      sx={[
        {
          ".MuiBackdrop-root": {
            background: "rgba(52, 64, 84, 0.7)",
          },
        },
      ]}
      disableAutoFocus={true}
    >
      <Box className={cssClasses.modalWrapper}>
        <div className={cssClasses.wrapperHeader}>
          <span className={cssClasses.closeIcon} onClick={closeModal} tabIndex={0}>
            <Close width={"1.2rem"} height={"1.2rem"} />
          </span>
          <div>Manual Tag</div>
        </div>

        <div className={cssClasses.wrapperContent}>
          <div className={cssClasses.mutedText}>
            Untagged Amount: ₹{numberWithCommas(transaction?.amount)} · Pay ID: {transaction?.payid || "-"}
          </div>

          {loadingDues ? (
            <div className={cssClasses.mutedText}>Checking this lead's pending tokens and allotments…</div>
          ) : (
            <>
              <div>
                <div className={cssClasses.sectionTitle}>Token</div>
                {pendingDues.tokens.length === 0 ? (
                  <div className={cssClasses.mutedText}>No pending token found for this lead.</div>
                ) : (
                  pendingDues.tokens.map((t) => {
                    const isSelected = t.eventTokenId === selectedTokenId;
                    return (
                      <div
                        key={t.paymentId || t.eventTokenId}
                        className={`${cssClasses.candidateCard} ${isSelected ? cssClasses.candidateCardSelected : ""}`}
                        role="radio"
                        aria-checked={isSelected}
                        onClick={() => {
                          // Selecting a different candidate (or deselecting) always clears the
                          // amount fields — they belong to whichever card is currently selected,
                          // never to the one that was previously selected.
                          setSelectedTokenId(isSelected ? null : t.eventTokenId);
                          setTokenAmount("");
                        }}
                      >
                        <div className={cssClasses.candidateHeader}>
                          <span
                            className={`${cssClasses.radioDot} ${isSelected ? cssClasses.radioDotSelected : ""}`}
                          />
                          <LeadStatusChip
                            label={"Token"}
                            icon={<Dot width={"0.5rem"} height={"0.5rem"} />}
                            style={{ color: "#3C3489", backgroundColor: "#EEEDFE" }}
                          />
                        </div>
                        <div className={cssClasses.mutedText}>
                          {t.tokenNumber || "-"} · owes ₹{numberWithCommas((t.amount || 0) - (t.paidAmount || 0))} · {t.status}
                        </div>
                        <div className={cssClasses.mutedText} style={{ fontSize: "1.1rem" }}>
                          Event Token ID: {t.eventTokenId}
                        </div>
                        {isSelected && (
                          <div className={cssClasses.amountRow} onClick={(e) => e.stopPropagation()}>
                            <div className={cssClasses.amountField}>
                              <CustomInput
                                inputType="number"
                                label="Token Amount"
                                placeholder="Enter amount"
                                value={tokenAmount}
                                handleChange={(v) => setTokenAmount(v)}
                                max={12}
                              />
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              <div>
                <div className={cssClasses.sectionTitle}>Allotment</div>
                {pendingDues.allotments.length === 0 ? (
                  <div className={cssClasses.mutedText}>No pending allotment found for this lead.</div>
                ) : (
                  pendingDues.allotments.map((a) => {
                    const isSelected = a.allotmentPaymentId === selectedAllotmentId;
                    const complete = a.status === "Payment Complete";
                    return (
                      <div
                        key={a.allotmentPaymentId}
                        className={`${cssClasses.candidateCard} ${isSelected ? cssClasses.candidateCardSelected : ""}`}
                        role="radio"
                        aria-checked={isSelected}
                        onClick={() => {
                          // Selecting a different candidate (or deselecting) always clears the
                          // amount/OCR/GST/SDR fields — they belong to whichever card is
                          // currently selected, never to the one that was previously selected.
                          setSelectedAllotmentId(isSelected ? null : a.allotmentPaymentId);
                          setAllotmentAmount("");
                          setOcr("");
                          setGst("");
                          setSdr("");
                        }}
                      >
                        <div className={cssClasses.candidateHeader}>
                          <span
                            className={`${cssClasses.radioDot} ${isSelected ? cssClasses.radioDotSelected : ""}`}
                          />
                          <LeadStatusChip
                            label={"Allotment"}
                            icon={<Dot width={"0.5rem"} height={"0.5rem"} />}
                            style={{ color: "#0C447C", backgroundColor: "#E6F1FB" }}
                          />
                        </div>
                        <div className={cssClasses.mutedText}>
                          Due for {a.dueFor} · owes ₹{numberWithCommas(a.dueAmount)} · {a.status}
                        </div>
                        <div className={cssClasses.mutedText} style={{ fontSize: "1.1rem" }}>
                          Allotment Payment ID: {a.allotmentPaymentId}
                        </div>
                        {isSelected && (
                          <div onClick={(e) => e.stopPropagation()}>
                            <div className={cssClasses.amountRow}>
                              <div className={cssClasses.amountField}>
                                <CustomInput
                                  inputType="number"
                                  label="Allotment Amount"
                                  placeholder="Enter amount"
                                  value={allotmentAmount}
                                  handleChange={(v) => setAllotmentAmount(v)}
                                  max={12}
                                />
                              </div>
                            </div>
                            {complete && (
                              <div className={cssClasses.amountRow} style={{ marginTop: "1rem" }}>
                                <div className={cssClasses.amountField}>
                                  <CustomInput
                                    inputType="number"
                                    label="OCR"
                                    placeholder="0"
                                    value={ocr}
                                    handleChange={(v) => setOcr(v)}
                                    max={12}
                                  />
                                </div>
                                <div className={cssClasses.amountField}>
                                  <CustomInput
                                    inputType="number"
                                    label="GST"
                                    placeholder="0"
                                    value={gst}
                                    handleChange={(v) => setGst(v)}
                                    max={12}
                                  />
                                </div>
                                <div className={cssClasses.amountField}>
                                  <CustomInput
                                    inputType="number"
                                    label="SDR"
                                    placeholder="0"
                                    value={sdr}
                                    handleChange={(v) => setSdr(v)}
                                    max={12}
                                  />
                                </div>
                              </div>
                            )}
                            {!complete && (
                              <div className={cssClasses.mutedText} style={{ marginTop: "0.4rem" }}>
                                This allotment hasn't reached Payment Complete yet — OCR/GST/SDR will be split
                                automatically once it does.
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              {errorMessage && <div className={cssClasses.errorBanner}>{errorMessage}</div>}
            </>
          )}
        </div>

        <div className={cssClasses.wrapperFooter}>
          <Button
            style={{
              color: "#344054",
              backgroundColor: "#ffffff",
              fontSize: "1.6rem",
              lineHeight: "2.4rem",
            }}
            btnName={"Cancel"}
            clicked={() => {
              closeModal();
            }}
          />
          <Button
            style={{
              color: "#ffffff",
              backgroundColor: "#155EEF",
              fontSize: "1.6rem",
              lineHeight: "2.4rem",
            }}
            btnName={"Submit"}
            clicked={() => {
              submit();
            }}
            disable={
              loadingDues ||
              submitting ||
              (pendingDues.tokens.length === 0 && pendingDues.allotments.length === 0)
            }
          />
        </div>
      </Box>
    </Modal>
  );
}
